"use client";

import React from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";

import Header from "@/components/header";
import Footer from "@/components/footer";
import ScrollToTop from "@/components/ScrollToTop";

import HomePage from "@/app/[locale]/page";
import AboutPage from "@/app/[locale]/about/page";
import ServicesPage from "@/app/[locale]/services/page";
import ServiceDetailPage from "@/app/[locale]/services/[slug]/page";
import SolutionDetailPage from "@/app/[locale]/solutions/[slug]/page";
import ContactPage from "@/app/[locale]/contact/page";
import FAQPage from "@/app/[locale]/faq/page";
import BlogPage from "@/app/[locale]/blog/page";
import BlogDetailPage from "@/app/[locale]/blog/[slug]/page";
import ExperienceDetailPage from "@/app/[locale]/experiences/[id]/page";

import AdminLoginPage from "@/app/admin/login/page";
import AdminCreateBlogPage from "@/app/admin/create-blog/page";
import RootNotFound from "@/app/not-found";
import { routing } from "@/i18n/routing";
import LocaleHtmlLang from "@/components/LocaleHtmlLang";
import SeoHreflang from "@/components/SeoHreflang";
import { normalizeLocale } from "@/lib/site";

function AsyncPageWrapper({ Component }) {
  const params = useParams();
  return <Component params={params} />;
}

function LocaleShell() {
  const { locale } = useParams();
  const location = useLocation();
  const rawLocale = Array.isArray(locale) ? locale[0] : locale;
  const isSupportedLocale = !!rawLocale && routing.locales.includes(rawLocale);

  if (!isSupportedLocale) {
    const segments = location.pathname.split("/").filter(Boolean);
    const rest = segments.slice(1).join("/");
    const redirectTo = `/${routing.defaultLocale}${rest ? `/${rest}` : ""}`;
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <>
      <LocaleHtmlLang locale={normalizeLocale(rawLocale)} />
      <SeoHreflang />
      <ScrollToTop />
      <Header />
      <Outlet />
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <Routes>
      <Route
        path="/"
        element={<Navigate to={`/${routing.defaultLocale}`} replace />}
      />

      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/create-blog" element={<AdminCreateBlogPage />} />

      <Route path="/:locale" element={<LocaleShell />}>
        <Route index element={<AsyncPageWrapper Component={HomePage} />} />
        <Route path="about" element={<AsyncPageWrapper Component={AboutPage} />} />
        <Route path="services" element={<AsyncPageWrapper Component={ServicesPage} />} />
        <Route
          path="services/:slug"
          element={<AsyncPageWrapper Component={ServiceDetailPage} />}
        />
        <Route
          path="solutions/:slug"
          element={<AsyncPageWrapper Component={SolutionDetailPage} />}
        />
        <Route path="contact" element={<AsyncPageWrapper Component={ContactPage} />} />
        <Route path="faq" element={<AsyncPageWrapper Component={FAQPage} />} />
        <Route path="blog" element={<AsyncPageWrapper Component={BlogPage} />} />
        <Route
          path="blog/:slug"
          element={<AsyncPageWrapper Component={BlogDetailPage} />}
        />
        <Route
          path="experiences/:id"
          element={<AsyncPageWrapper Component={ExperienceDetailPage} />}
        />
      </Route>

      <Route path="*" element={<RootNotFound />} />
    </Routes>
  );
}
